import type { FocusEvent, KeyboardEvent } from 'react';

/**
 * Mobile-friendly form navigation. There is no Tab key on a phone keyboard, so
 * without help the Return key does nothing and the user must hand-tap each
 * field. Attach both handlers to a `<form>` or any element that wraps the
 * fields:
 *
 *   <form onKeyDown={focusNextOnEnter} onFocus={applyEnterKeyHint}> … </form>
 *   <div  onKeyDown={focusNextOnEnter} onFocus={applyEnterKeyHint}> … </div>
 *
 * - `focusNextOnEnter`  — Return jumps to the next field (submits on the last).
 * - `applyEnterKeyHint` — labels the Return key "Next" while another field
 *   follows, "Done" on the last one.
 *
 * Both ignore `<textarea>` (Enter = newline), `<select>`, non-text inputs and
 * IME composition, and scope to the input's owning `<form>` when present,
 * otherwise to the element the handler is attached to (`currentTarget`).
 */

type FieldElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const TEXTUAL_TYPES = new Set([
  'text',
  'email',
  'tel',
  'url',
  'password',
  'search',
  'number',
]);

function isTextualInput(el: EventTarget | null): el is HTMLInputElement {
  return el instanceof HTMLInputElement && TEXTUAL_TYPES.has(el.type);
}

/** Visible, enabled, tabbable fields in DOM order within the given scope. */
function collectFields(scope: HTMLElement): FieldElement[] {
  return Array.from(scope.querySelectorAll<FieldElement>('input, select, textarea')).filter(
    (field) => {
      if (field instanceof HTMLInputElement && field.type === 'hidden') return false;
      if (field.disabled) return false;
      if (
        (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) &&
        field.readOnly
      ) {
        return false;
      }
      if (field.tabIndex < 0) return false;
      return field.getClientRects().length > 0; // rendered & visible
    },
  );
}

function nextFieldAfter(current: HTMLInputElement, currentTarget: HTMLElement): FieldElement | undefined {
  const scope: HTMLElement = current.form ?? currentTarget;
  const fields = collectFields(scope);
  const index = fields.indexOf(current);
  if (index === -1) return undefined;
  return fields[index + 1];
}

export function focusNextOnEnter(event: KeyboardEvent<HTMLElement>): void {
  if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
    return;
  }
  if (!isTextualInput(event.target)) return;

  const next = nextFieldAfter(event.target, event.currentTarget as HTMLElement);
  if (!next) return; // last field — leave native submit / action button to it

  // Prevent the implicit form submit that Enter would otherwise trigger, so the
  // keypress moves to the next field instead.
  event.preventDefault();
  next.focus();
}

/**
 * Set the on-screen Return key label for the focused field: "next" when another
 * field follows, "done" on the last. Wired via `onFocus` (which bubbles in
 * React) so it tracks the active field across conditional/step UIs.
 */
export function applyEnterKeyHint(event: FocusEvent<HTMLElement>): void {
  if (!isTextualInput(event.target)) return;
  const next = nextFieldAfter(event.target, event.currentTarget as HTMLElement);
  event.target.enterKeyHint = next ? 'next' : 'done';
}
